/**
 * Harbour paints the Spartacus venue notebook from GET /venues/:id.
 * There is no second note store — sort and labels match Spartacus.
 */

import { HARBOUR_NOTES_TITLE } from 'src/constants/harbour'
import {
  SPARTACUS_NOTE_DEFAULT_OUTCOME,
  SPARTACUS_NOTE_DEFAULT_TYPE,
} from 'src/constants/spartacusNotes'
import dayjs from 'dayjs'
import {
  formatSpartacusUkShortDate,
  mapSpartacusVenueNote,
  mapSpartacusVenueNotesResponse,
  sortSpartacusVenueNotes,
} from './spartacusMappers'

function asNotesPayload(value) {
  if (Array.isArray(value)) {
    return { notes: value }
  }
  if (value && typeof value === 'object') {
    return value
  }
  return {}
}

export function mapHarbourSpartacusNotes(drawerNotes) {
  const mapped = mapSpartacusVenueNotesResponse(asNotesPayload(drawerNotes))
  return {
    venueId: mapped.venueId,
    notes: sortSpartacusVenueNotes(mapped.notes),
    pagination: mapped.pagination,
  }
}

export function getHarbourSpartacusNotes(drawer) {
  return mapHarbourSpartacusNotes(drawer?.notes).notes
}

export function formatHarbourNoteWhen(createdAt) {
  const date = formatSpartacusUkShortDate(createdAt)
  if (date === '—') {
    return date
  }
  const parsed = dayjs(createdAt)
  if (!parsed.isValid()) {
    return date
  }
  return `${date} · ${parsed.format('HH:mm')}`
}

/**
 * Prefer GET pagination.total so Harbour N matches Spartacus
 * Customer notes (N). Never show a total lower than the notes on screen.
 */
export function getHarbourNotesTotal(notes, pagination) {
  const shown = Array.isArray(notes) ? notes.length : 0
  if (pagination?.total == null || pagination.total === '') {
    return shown
  }
  const total = Number(pagination.total)
  if (!Number.isFinite(total) || total < 0) {
    return shown
  }
  return Math.max(total, shown)
}

export function getHarbourNotesOverflowCount(shown, total) {
  const shownCount = Number(shown) || 0
  const totalCount = Number(total) || 0
  if (shownCount <= 0 || totalCount <= shownCount) {
    return 0
  }
  return totalCount - shownCount
}

export function formatHarbourNotesTitle(total) {
  const count = Number(total)
  if (!Number.isFinite(count) || count < 0) {
    return HARBOUR_NOTES_TITLE
  }
  return `${HARBOUR_NOTES_TITLE} (${count})`
}

export function formatHarbourNotesOverflowCaption(shown, total) {
  const remaining = getHarbourNotesOverflowCount(shown, total)
  if (remaining <= 0) {
    return null
  }
  return `+${remaining} more in Spartacus`
}

export function buildHarbourSpartacusNoteCreateBody({
  noteText = '',
  pinned = false,
} = {}) {
  return {
    noteText: String(noteText || '').trim(),
    noteType: SPARTACUS_NOTE_DEFAULT_TYPE,
    outcome: SPARTACUS_NOTE_DEFAULT_OUTCOME,
    followUpAt: null,
    pinned: Boolean(pinned),
  }
}

function asSpartacusNote(incoming) {
  if (!incoming || typeof incoming !== 'object') {
    return null
  }
  if (incoming.note && typeof incoming.note === 'object') {
    return incoming.note
  }
  return incoming
}

export function mergeHarbourSpartacusNote(notes, incoming) {
  const mapped = mapSpartacusVenueNote(asSpartacusNote(incoming))
  if (!mapped?.noteId) {
    return sortSpartacusVenueNotes(notes || [])
  }
  return sortSpartacusVenueNotes([
    mapped,
    ...(notes || []).filter(note => note.noteId !== mapped.noteId),
  ])
}

export function replaceHarbourSpartacusNote(notes, incoming) {
  const mapped = mapSpartacusVenueNote(asSpartacusNote(incoming))
  if (!mapped?.noteId) {
    return sortSpartacusVenueNotes(notes || [])
  }
  const current = notes || []
  if (!current.some(note => note.noteId === mapped.noteId)) {
    return mergeHarbourSpartacusNote(current, mapped)
  }
  return sortSpartacusVenueNotes(
    current.map(note => (note.noteId === mapped.noteId ? mapped : note)),
  )
}
